import { createYProxy } from '@blocksuite/store';
import {
  assembleDatabaseShardDirectory,
  type DatabaseShardDirectoryHeadChange,
  databaseShardDirectoryMap,
  databaseShardDirectoryPartDocIds,
  type DatabaseShardDirectoryPartDocument,
  databaseShardDirectoryPartMap,
  type DatabaseShardDirectoryPartMaps,
  type DatabaseShardDirectoryStoredHead,
  type DatabaseShardManifest,
  normalizeDatabaseShardDirectoryHead,
  planDatabaseShardDirectoryHeadChanges,
  planDatabaseShardDirectoryWrite,
  writeDatabaseShardDirectoryHead,
} from '@streamlain/database-identity';
import { PublicationError } from '@streamlain/doc-publication-contract';
import type { Map as YMap } from 'yjs';

import type { DatabasePublicationStage } from './data-source-publication.js';

type HeadRecord = Record<string, unknown>;

/**
 * Applies head changes through the block's cached reactive proxy: it ignores
 * local non-proxy transactions, so raw nested Yjs writes would leave the
 * directory prop of the model stale for every later local reader.
 */
function applyHeadChanges(
  block: YMap<unknown>,
  changes: readonly DatabaseShardDirectoryHeadChange[]
): void {
  if (!changes.length) return;
  const live = createYProxy<HeadRecord>(
    databaseShardDirectoryMap(block) as YMap<unknown>
  );
  const parts = live.parts as HeadRecord[];
  for (const change of changes) {
    switch (change.kind) {
      case 'field':
        live[change.key] = change.value;
        break;
      case 'parts':
        parts.splice(
          change.index,
          change.deleteCount,
          ...change.insert.map(part => ({ ...part }))
        );
        break;
      case 'part-field':
        // An undefined value removes the field: the part became the open one.
        if (change.value === undefined) delete parts[change.index]![change.key];
        else parts[change.index]![change.key] = change.value;
        break;
    }
  }
}

/**
 * Stores `manifest` as the directory of the database block `block`: the head
 * on the block, the shard entries in the part documents of `partMaps`.
 * Returns the part documents it wrote; the caller publishes them with the
 * block document in one publication.
 */
export function writeDatabaseRootDirectory(
  block: YMap<unknown>,
  partMaps: DatabaseShardDirectoryPartMaps,
  manifest: DatabaseShardManifest
): string[] {
  const write = planDatabaseShardDirectoryWrite(
    databaseShardDirectoryMap(block),
    partMaps,
    manifest
  );
  write.writeParts(partMaps);
  if (!write.stored) writeDatabaseShardDirectoryHead(block, write.head);
  else
    applyHeadChanges(
      block,
      planDatabaseShardDirectoryHeadChanges(write.stored, write.storedHead)
    );
  return write.writtenPartDocIds;
}

/** Head of the directory stored on `block`; any other form is a format mismatch. */
export function readPublicationDirectoryHead(
  block: YMap<unknown>
): DatabaseShardDirectoryStoredHead {
  let head: DatabaseShardDirectoryStoredHead | undefined;
  try {
    head = normalizeDatabaseShardDirectoryHead(
      databaseShardDirectoryMap(block)
    );
  } catch {
    throw new PublicationError('FORMAT_MISMATCH');
  }
  if (!head) throw new PublicationError('FORMAT_MISMATCH');
  return head;
}

/** Part documents the directory head on `block` names, in directory order. */
export function readPublicationDirectoryPartDocIds(
  block: YMap<unknown>
): string[] {
  return databaseShardDirectoryPartDocIds(readPublicationDirectoryHead(block));
}

/**
 * Part maps of a publication: every part is a member of `stage`; a part the
 * write creates joins the stage as a new document.
 */
export function publicationDirectoryPartMaps(
  stage: DatabasePublicationStage
): DatabaseShardDirectoryPartMaps {
  return partDocId =>
    databaseShardDirectoryPartMap(
      stage.ids().includes(partDocId)
        ? stage.doc(partDocId)
        : stage.addEmptyDoc(partDocId)
    ) as YMap<unknown>;
}

/** Documents a publication holds by id; undefined for any other. */
export type PublicationDirectoryDocs = (
  partDocId: string
) => DatabaseShardDirectoryPartDocument | undefined;

/** The documents `stage` holds. */
export function publicationStageDocs(
  stage: DatabasePublicationStage
): PublicationDirectoryDocs {
  return partDocId =>
    stage.ids().includes(partDocId) ? stage.doc(partDocId) : undefined;
}

/**
 * Complete directory of the database block `block` in a publication whose
 * documents `docs` hold all of its parts.
 */
export function readPublicationDirectory(
  block: YMap<unknown>,
  docs: PublicationDirectoryDocs
): DatabaseShardManifest {
  const head = readPublicationDirectoryHead(block);
  const read = assembleDatabaseShardDirectory(head, partDocId => {
    const doc = docs(partDocId);
    return doc ? databaseShardDirectoryPartMap(doc) : undefined;
  });
  if (read.status !== 'ready')
    throw new PublicationError('INCOMPLETE_LOCAL_BASE');
  return read.manifest;
}
